import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../../../components/base/Modal";
import type { Unit } from "../../../../../../store/admin/unitsStore";
import type { Trailer } from "../../../../../../store/admin/trailersStore";
import { getAvailableTrailers } from "../../../../../../store/admin/trailersStore";
import type { ThemeClasses } from "../../../../../../types/components";

interface TrailerConnectionModalProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  unit: Unit;
  onClose: () => void;
  onConnect: (trailerId: string) => Promise<void>;
  onDisconnect: (trailerId: string) => Promise<void>;
}

export default function TrailerConnectionModal({
  themeClasses,
  isDarkMode,
  unit,
  onClose,
  onConnect,
  onDisconnect,
}: TrailerConnectionModalProps) {
  const { t } = useTranslation("adminSections");
  const [availableTrailers, setAvailableTrailers] = useState<Trailer[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingTrailers, setLoadingTrailers] = useState(false);
  const [selectedTrailer, setSelectedTrailer] = useState<Trailer | null>(null);

  const hasConnectedTrailer = !!unit.remolque_id || !!unit.remolque;

  useEffect(() => {
    if (!hasConnectedTrailer) {
      fetchAvailableTrailers();
    }
  }, [hasConnectedTrailer]);

  const fetchAvailableTrailers = async () => {
    setLoadingTrailers(true);
    try {
      const trailers = await getAvailableTrailers();
      setAvailableTrailers(trailers);
    } catch (error) {
      console.error("Error fetching available trailers:", error);
    } finally {
      setLoadingTrailers(false);
    }
  };

  const handleConnect = async () => {
    if (!selectedTrailer) return;
    setLoading(true);
    try {
      await onConnect(selectedTrailer.id);
      onClose();
    } catch (error) {
      console.error("Error connecting trailer:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    const trailerId = unit.remolque_id || unit.remolque?.id;
    if (!trailerId) return;
    setLoading(true);
    try {
      await onDisconnect(trailerId);
      onClose();
    } catch (error) {
      console.error("Error disconnecting trailer:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderDisconnectView = () => (
    <div className="space-y-4">
      <div className={`p-4 rounded-lg border ${themeClasses.cardBorder} ${isDarkMode ? "bg-dark-card" : "bg-gray-50"}`}>
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-primary-500/20 rounded-lg">
            <i className="ri-caravan-line text-2xl text-primary-500"></i>
          </div>
          <div>
            <p className={`font-semibold ${themeClasses.text}`}>
              {unit.remolque?.name || t("units.trailer.connectedTrailer")}
            </p>
            <p className={`text-sm ${themeClasses.textSecondary}`}>
              {unit.remolque?.plate ? unit.remolque.plate + " " + t("units.fields.plate") : null}
            </p>
          </div>
        </div>
      </div>

      <p className={`text-center ${themeClasses.textSecondary}`}>
        {t("units.trailer.disconnectConfirmation")}
      </p>

      <div className="grid grid-cols-2 gap-4 pt-2">
        <button
          type="button"
          onClick={onClose}
          className={`py-3 px-4 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} font-medium transition-colors`}
        >
          {t("units.form.cancel")}
        </button>
        <button
          type="button"
          onClick={handleDisconnect}
          disabled={loading}
          className={`py-3 px-4 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors ${
            loading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {loading ? t("units.trailer.disconnecting") : t("units.trailer.disconnect")}
        </button>
      </div>
    </div>
  );

  const renderConnectView = () => (
    <div className="space-y-4">
      {loadingTrailers ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : availableTrailers.length === 0 ? (
        <div className={`text-center py-8 ${themeClasses.textSecondary}`}>
          <i className="ri-caravan-line text-4xl mb-2 block opacity-50"></i>
          <p>{t("units.trailer.noAvailableTrailers")}</p>
        </div>
      ) : (
        <>
          <p className={`text-sm ${themeClasses.textSecondary}`}>
            {t("units.trailer.selectTrailer")}
          </p>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {availableTrailers?.map((trailer) => (
              <div
                key={trailer.id}
                onClick={() => setSelectedTrailer(trailer)}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedTrailer?.id === trailer.id
                    ? "border-primary-500 bg-primary-500/10"
                    : `${themeClasses.cardBorder} ${isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50"}`
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${selectedTrailer?.id === trailer.id ? "bg-primary-500/20" : isDarkMode ? "bg-dark-border" : "bg-gray-100"}`}>
                    <i className={`ri-caravan-line text-lg ${selectedTrailer?.id === trailer.id ? "text-primary-500" : themeClasses.text}`}></i>
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${themeClasses.text}`}>
                      {trailer.name || trailer.plate}
                    </p>
                    <p className={`text-xs ${themeClasses.textSecondary}`}>
                      {trailer.brand} {trailer.model} • {trailer.plate}
                    </p>
                  </div>
                  {selectedTrailer?.id === trailer.id && (
                    <i className="ri-checkbox-circle-fill text-primary-500 text-xl"></i>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="grid grid-cols-2 gap-4 pt-2">
        <button
          type="button"
          onClick={onClose}
          className={`py-3 px-4 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} font-medium transition-colors`}
        >
          {t("units.form.cancel")}
        </button>
        <button
          type="button"
          onClick={handleConnect}
          disabled={loading || !selectedTrailer}
          className={`py-3 px-4 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg font-medium transition-colors ${
            loading || !selectedTrailer ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {loading ? t("units.trailer.connecting") : t("units.trailer.connect")}
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      themeClasses={themeClasses}
      onClose={onClose}
      title={hasConnectedTrailer ? t("units.trailer.manageTrailer") : t("units.trailer.connectTrailer")}
      width="max-w-md"
      content={
        <div>
          <div className={`mb-4 p-3 rounded-lg border ${themeClasses.cardBorder} ${isDarkMode ? "bg-dark-card" : "bg-gray-50"}`}>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <i className="ri-truck-line text-lg text-blue-500"></i>
              </div>
              <div>
                <p className={`font-medium text-sm ${themeClasses.text}`}>{unit.name}</p>
                <p className={`text-xs ${themeClasses.textSecondary}`}>{unit.plate}</p>
              </div>
            </div>
          </div>

          {hasConnectedTrailer ? renderDisconnectView() : renderConnectView()}
        </div>
      }
    />
  );
}
